import { LinearProgressProps, List, ListItem, styled, Typography } from "@mui/material";
import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import { ChevronRight, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  CompletitionStatus,
  Course, CourseInfo,
  RemainingTime,
  TypographyContainer,
  TypographyWidget,
  TypographyCourseType
} from "./KeepLearning.styles";
import { KeepLearningCourse } from "./KeepLearningCourse.types";
import { KeepLearningWidgetProps } from "./KeepLearningWidget.component";
import { handleLevel } from "./utils";
import { ButtonDigited, StyledTypography } from "../../components/Button/Button.variants";
import { CONTENTS_NAME, NAVBAR_ITEMS, PAGE_NAME } from "../../consts";
import { useContentQuery, useStructuresQuery } from "../../queries";
import { useLanguageStore, useNavbarStore } from "../../stores";
import { TitleTypography } from "../../styles/CommonWidgetStyles/Typography";
import type { LearningObject } from "../../types";
import { formatDuration_h_m } from "../../utils/datetime";
import { NAVIGATION } from "../../utils/navigationRoutes";
import { handleType } from "../AgendaWidget/utils";
import { WidgetSkeleton } from "../WidgetSkeleton/WidgetSkeleton.component";

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== "isCompleted"
})<LinearProgressProps & { isCompleted: boolean }>(({
  theme,
  isCompleted
}) => ({
  borderRadius: "0.25rem",
  height: "0.1875rem",
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.customColors.border
  },
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: isCompleted ? theme.customColors.backgroundSecondaryComplete : theme.customColors.exploreCardText,
    borderRadius: 5
  }
}));

export const KeepLearningContainer = ({ setIsOpened,enabled = true  }: KeepLearningWidgetProps) => {
  const language = useLanguageStore(state => state.language)?.id;
  const isRTL = language === "ar";
  const { t } = useTranslation();
  const theme = useTheme();

  const navigate = useNavigate();
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);

  const { data: pageStructureWidgetKeepLearning, isFetching: structureFetching } = useStructuresQuery({
    enabled,
    pageName: PAGE_NAME.WIDGET_ONGOING
  });

  const pageStructureData = pageStructureWidgetKeepLearning?.relativePaths?.[0];

  const { data: onGoingCoursesQueryRes, isFetching: isFetchingOnGoingCourses } = useContentQuery<Record<string, LearningObject[]>>({
    enabled: enabled && !!pageStructureData?.apiPath,
    path: `${pageStructureData?.apiPath}?pageSize=4&pageNumber=0`,
    queryKey: [language ?? ""]
  });

  const [onGoingCourses, setOnGoingCourses] = useState<KeepLearningCourse[]>([]);

  const handleProgress = (courseDuration: number, percentageOfCompletion: string) => {
    const numericPct = parseFloat(String(percentageOfCompletion).trim().replace(/%/g, "")) || 0;
    const progress = Math.floor((numericPct / 100) * courseDuration);
    return progress;
  };

  useEffect(() => {
    if (onGoingCoursesQueryRes && onGoingCoursesQueryRes?.[CONTENTS_NAME.ON_GOING]?.length > 0) {
      const mappedList = onGoingCoursesQueryRes[CONTENTS_NAME.ON_GOING]
        ?.map((onGoingObject) => {
          const course: KeepLearningCourse = {
            categories: onGoingObject.topicTags,
            coverPublicURL: onGoingObject.coverPublicURL,
            description: onGoingObject.shortDescription,
            duration: onGoingObject.duration,
            id: onGoingObject.id,
            learningObjectType: onGoingObject.learningObjectType,
            learningObjectTypology: onGoingObject.learningObjectTypology,
            percentageOfCompletion: onGoingObject.percentageOfCompletion,
            progress: handleProgress(onGoingObject.duration, onGoingObject.percentageOfCompletion),
            status: onGoingObject.status,
            title: onGoingObject.title
          };
          return course;
        }) ?? [];
      setOnGoingCourses(mappedList);
    }
  }, [onGoingCoursesQueryRes]);

  const calculateProgress = (progress: number, duration: number) => {
    if (!progress || !duration) return 1;
    if (duration === 0) return 1;
    // return 1 to show a small progress sign in the progress bar when progress is 0
    if (progress / duration === 0) {
      return 1;
    }

    return Math.floor((progress / duration) * 100);
  };

  const isCompleted = (progress: number) => progress === 100;

  const showAllCourses = (e: React.MouseEvent) => {
    e.preventDefault();
    if(!pageStructureData) return;
    if(setIsOpened) setIsOpened(false);
    navigate(`/esplora/categories/${pageStructureData?.title ?? ""}`, {
      state: {
        relativePath: pageStructureData,
        topicsQueryString: "",
        titleCards: pageStructureData.title
      }
    });
  };

  return (
    <>
      { isFetchingOnGoingCourses || structureFetching ? (
        <WidgetSkeleton />
      ) : (
        <>
          <TitleTypography aria-label={ t("keep_learning") + "-widget" }>{ t("keep_learning") }</TitleTypography>
          { onGoingCourses && onGoingCourses.length === 0 && (
            <Typography tabIndex={ 0 } sx={ { color:theme.customColors.exploreCardText } } fontSize="0.85rem" fontWeight="400" paddingLeft="1rem" >
              { t("no_activities_in_progress") }
            </Typography>
          ) }
          <List disablePadding>
            { onGoingCourses.map((learningObject: KeepLearningCourse, index: number) => {
              const courseType = handleType(learningObject.learningObjectType, learningObject.learningObjectTypology);
              return (
                <ListItem
                  disablePadding
                  key={ `course-${index}--${language}` }
                  tabIndex={ 0 } // make focusable
                  aria-label={ `${t("keep_learning")}  ${courseType} ${learningObject.title} ${t("completion_percentage")}${learningObject.percentageOfCompletion}` }
                >
                  <Course
                    sx={ { width: "100%" } }
                    onClick={ () => {
                      navigate(NAVIGATION.FOR_YOU_DETAIL
                        .replace(":id", learningObject.id.toString())
                        .replace(":learningObjectTypology", learningObject.learningObjectTypology));
                      setNavbarItem(NAVBAR_ITEMS.EXPLORE);
                    } }
                  >
                    <TypographyContainer>
                      { /* header */ }
                      <CourseInfo
                        role="presentation"
                        aria-labelledby={ "course-type-" + courseType + "-" + learningObject.id }
                      >
                        <div style={ { flexDirection: isRTL ? "row-reverse" : "row" } }>
                          <TypographyCourseType
                            color={ theme.customColors.exploreCardText }
                            id={ "course-type-" + courseType + "-" + learningObject.id }
                            aria-label={ courseType }
                            $isRTL={ isRTL }
                          >
                            { courseType }
                          </TypographyCourseType>

                          <Typography
                            color={ theme.customColors.exploreCardText }
                            fontSize="0.875rem"
                            fontWeight={ 600 }
                            lineHeight="1.09375rem"
                            id={ "course-type-" + courseType + "-" + learningObject.id }
                          >
                            { t(handleLevel(learningObject.learningObjectTypology)) }
                          </Typography>
                        </div>
                        { learningObject.duration > 0 && (
                          <RemainingTime>
                            <Clock id={ "iconClock-" + learningObject.id } size="0.875rem" fontWeight="500" color={ theme.customColors.textTag } />
                            { " " }
                            <Typography fontSize="0.75rem" fontWeight="600" color={ theme.customColors.textTag }>
                              { formatDuration_h_m(learningObject.duration) }
                            </Typography>
                          </RemainingTime>
                        ) }

                      </CourseInfo>

                      { /* title course */ }
                      <TypographyWidget
                        color={ theme.customColors.exploreCardText }
                        id={ "course-title" + learningObject.id }
                        $isRTL={ isRTL }
                        textAlign={ isRTL ? "right" : "left" }
                      >
                        { learningObject.title }
                      </TypographyWidget>

                      { /* progress bar */ }
                      <BorderLinearProgress
                        variant="determinate"
                        isCompleted={ isCompleted(learningObject.progress) }
                        value={ calculateProgress(learningObject.progress, learningObject.duration) }
                        aria-labelledby="widget-keep-learning-progress-percentage"
                      />

                      { /* completition status */ }
                      <CompletitionStatus
                        aria-labelledby={ "widget-keep-learning-progress-percentage" }
                        style={ { flexDirection: isRTL ? "row-reverse" : "row" } }
                      >
                        <TypographyWidget textAlign={ isRTL ? "right" : "left" }>
                          { t("completion") }
                        </TypographyWidget>
                        <TypographyWidget
                          id="widget-keep-learning-progress-percentage"
                          textAlign={ isRTL ? "left" : "end" }
                        >
                          { learningObject.percentageOfCompletion }
                        </TypographyWidget>
                      </CompletitionStatus>
                    </TypographyContainer>
                  </Course>
                </ListItem>
              );
            }) }
          </List>
          <ButtonDigited
            onClick={ showAllCourses }
            aria-label={  t("view_all") }
            typology="secondary" buttonSize="sm" selfPosition="flex-end" sx={ { marginRight:"1rem" } }
          >
            <>
              <StyledTypography>{ t("view_all") + " " }</StyledTypography> <ChevronRight size="0.875rem" />
            </>
          </ButtonDigited>
        </>
      ) }

    </>
  );
};

